/**
 * InProgressSessionBanner component
 * The Overview "Live now" hero card. Shown when a child currently has a tutoring
 * session in progress: child name, when it started, elapsed time, question
 * progress, and a derived progress bar. Renders nothing when no session is active.
 *
 * Implements US-008 (T2) / US-009 (T1): informational only — NO action controls.
 * Redesign (2026-06): warm gradient hero card with a circular avatar, a pulsing
 *   "Live now" marker, and a progress bar derived from questionProgress. All
 *   asserted strings/roles are preserved verbatim (role="status",
 *   "{name} is in a session right now", "Started today at HH:MM" / "Started at HH:MM",
 *   "Started {elapsed}", and the raw questionProgress text) so US-009 / US-008 /
 *   sprint-qa tests remain valid. AC-2: still zero buttons/links/action controls
 *   when no sessionId is present (back-compat, no-placeholder rule).
 *
 * Modified TS-007 (T8, SR6, AC-TS007.4): extended into the interactive live card
 *   (US-006). When session.sessionId is present, an "End session" action renders
 *   below the progress area; tapping it OPENS EndSessionConfirmDialog (T7) — it does
 *   NOT end immediately (BR-TS007.2). Confirming calls useEndSession (T3), which
 *   invalidates the parent caches on success so this card disappears on next refetch
 *   (no navigation — TO-LL-013). Degrades to informational-only (no button) when
 *   sessionId is absent, exactly matching the original US-008/US-009 contract.
 */

import { useState } from 'react';
import studentAvatar from '@/assets/student-avatar.png';
import { formatRelativeTime } from '@/lib/formatters';
import { Button } from '@/components/ui/button';
import { EndSessionConfirmDialog } from '@/components/EndSessionConfirmDialog';
import { useEndSession } from '@/hooks/useEndSession';
import type { InProgressSession } from '@/types/coins';

// ─── Error message mapping ────────────────────────────────────────────────────

/**
 * Map a raw useEndSession error code to the SA-resolved parent-facing message.
 * TO-LL-036: never surfaces raw backend error text.
 */
function mapEndSessionError(code: string | undefined): string {
  if (code === 'CONFLICT') {
    return 'This session is no longer active.';
  }
  return "Couldn't end the session. Please try again.";
}

// ─── Props ────────────────────────────────────────────────────────────────────

export interface InProgressSessionBannerProps {
  /** The active in-progress session, or null/undefined to render nothing */
  session: InProgressSession | null | undefined;
  /**
   * first-run-guide (T8): open the live session. Optional — when omitted the card keeps its
   * previous behaviour (End only), so every existing render site is unaffected.
   */
  onOpenSession?: (sessionId: string) => void;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

/**
 * Format the session startedAt ISO string into a human-friendly label.
 * Returns null on any parse/format error — banner omits the sub-label rather than crash.
 */
function formatStartedAt(startedAt: string): string | null {
  try {
    const date = new Date(startedAt);
    if (isNaN(date.getTime())) return null;

    const today = new Date();
    const isToday =
      date.getFullYear() === today.getFullYear() &&
      date.getMonth() === today.getMonth() &&
      date.getDate() === today.getDate();

    const timeStr = date.toLocaleTimeString('en-SG', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    });

    return isToday ? `Started today at ${timeStr}` : `Started at ${timeStr}`;
  } catch {
    return null;
  }
}

/**
 * Derive a 0–100 progress percentage from a "… X of Y" progress string.
 * Returns null when the string has no parseable "X of Y" pair (bar is then hidden).
 */
function parseProgressPercent(progress: string): number | null {
  const m = progress.match(/(\d+)\s*of\s*(\d+)/i);
  if (!m) return null;
  const current = parseInt(m[1], 10);
  const total = parseInt(m[2], 10);
  if (!total || total <= 0 || isNaN(current)) return null;
  return Math.max(0, Math.min(100, Math.round((current / total) * 100)));
}

// ─── InProgressSessionBanner (main export) ────────────────────────────────────

export function InProgressSessionBanner({
  session,
  onOpenSession,
}: InProgressSessionBannerProps) {
  // TS-007 (T8): hooks must be called unconditionally on every render (Rules of
  // Hooks) — called here, BEFORE the early-return below, so the hook order never
  // changes as `session` flips between null/undefined and a real value. The empty
  // string sessionId fallback is inert: the End action (and useEndSession.endSession)
  // is only ever rendered/invoked when session.sessionId is truthy (guarded below).
  const [confirmOpen, setConfirmOpen] = useState(false);
  const { endSession, isEnding, error, reset } = useEndSession(session?.sessionId ?? '');
  // No explicit "close dialog on success" effect is needed here: useEndSession's
  // onSuccess (T3) invalidates ['parent','dashboard'] / ['parent','live-sessions'],
  // which causes the PARENT page (ParentDashboardPage / LiveSessionsPage) to stop
  // rendering this exact session once the refetch resolves — unmounting this whole
  // component (and its dialog + confirmOpen state) along with it. On failure the
  // dialog correctly stays open (error is surfaced below) since no invalidation
  // happens on a failed mutation (TO-LL-036).

  // Early-return: render nothing when no session or missing required field (AC-3).
  if (!session || !session.childDisplayName) {
    return null;
  }

  const startedLabel = formatStartedAt(session.startedAt);

  const elapsedLabel = (() => {
    try {
      return formatRelativeTime(session.startedAt);
    } catch {
      return null;
    }
  })();

  const pct = session.questionProgress ? parseProgressPercent(session.questionProgress) : null;

  return (
    <div
      role="status"
      aria-label="Session in progress"
      className="relative flex h-full flex-col overflow-hidden rounded-3xl border border-orange-200 bg-gradient-to-br from-orange-50 via-amber-50 to-orange-100 p-6 shadow-lg"
    >
      {/* Decorative glow — purely visual */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute -right-10 -top-10 h-44 w-44 rounded-full bg-otter-orange-400/20 blur-2xl"
      />

      {/* "Live now" marker (decorative) */}
      <div className="relative z-[1] mb-4 flex items-center gap-2">
        <span className="relative flex h-2.5 w-2.5">
          <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-student-500 opacity-70" />
          <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-student-500" />
        </span>
        <span className="text-[11px] font-extrabold uppercase tracking-wider text-student-700">
          Live now
        </span>
      </div>

      {/* Child + status line */}
      <div className="relative z-[1] flex items-center gap-4">
        <img
          src={studentAvatar}
          alt=""
          aria-hidden="true"
          className="h-16 w-16 flex-shrink-0 rounded-full border-4 border-white bg-white object-cover shadow-md"
        />
        <div className="min-w-0">
          {/* AC-1: status line — string preserved for tests */}
          <p className="font-display text-xl font-semibold leading-tight text-otter-earth-800">
            {session.childDisplayName} is in a session right now
          </p>
          {/* Start time — omitted if unparseable */}
          {startedLabel && (
            <p className="mt-0.5 text-xs font-semibold text-orange-700/80">{startedLabel}</p>
          )}
          {/* Elapsed — omitted if formatRelativeTime throws */}
          {elapsedLabel && (
            <p className="text-xs font-semibold text-orange-700/80">Started {elapsedLabel}</p>
          )}
        </div>
      </div>

      {/* Progress — pinned to the bottom so the card balances the wallet height */}
      {session.questionProgress && (
        <div className="relative z-[1] mt-auto pt-5">
          <div className="mb-1.5 flex items-baseline justify-between">
            <span className="text-[13px] font-extrabold text-otter-earth-700">
              {session.questionProgress}
            </span>
            {pct != null && (
              <span className="text-xs font-bold text-orange-700/70">{pct}% complete</span>
            )}
          </div>
          {pct != null && (
            <div className="h-2.5 w-full overflow-hidden rounded-full bg-orange-200/70">
              <div
                className="h-full rounded-full bg-gradient-to-r from-otter-orange-400 to-otter-orange-600"
                style={{ width: `${pct}%` }}
              />
            </div>
          )}
        </div>
      )}

      {/* TS-007 (T8, AC-TS007.4): End action — ONLY rendered when session.sessionId
          exists (no-placeholder rule: never render a dead button). Tapping it OPENS
          the confirmation dialog (BR-TS007.2: no one-click end); the session is only
          actually ended when the parent confirms inside the dialog. */}
      {session.sessionId && (
        <div className="relative z-[1] mt-4 flex flex-col gap-2 sm:flex-row">
          {/*
            first-run-guide (T8): a way INTO the session, not only a way to kill it.
            Until now this card could only END a live session, so a parent whose child
            started one and wandered off had exactly one option, and it was destructive.

            Entering is client-side navigation and makes no request, so nothing here can
            validate. Whether she may actually answer is decided by `student.canAnswer` on
            the session payload and handled on arrival — see SessionChatPage. Do NOT try to
            pre-judge it here; that would be a second copy of a server rule, and it would
            drift.
          */}
          {onOpenSession && (
            <Button
              type="button"
              size="sm"
              variant="warm"
              className="h-11 w-full"
              onClick={() => onOpenSession(session.sessionId!)}
            >
              Go to {session.childDisplayName ? `${session.childDisplayName}'s` : 'the'} session
            </Button>
          )}
          <Button
            type="button"
            size="sm"
            variant={onOpenSession ? 'outline' : 'warm'}
            className="h-11 w-full"
            onClick={() => {
              reset();
              setConfirmOpen(true);
            }}
          >
            End session
          </Button>
        </div>
      )}

      {/* TS-007 (T8): shared end-flow dialog — same EndSessionConfirmDialog +
          useEndSession pairing used by the /parent/live-sessions listing page (T9),
          so both surfaces behave identically (CCM-003 same_behavior). */}
      <EndSessionConfirmDialog
        isVisible={confirmOpen}
        isSubmitting={isEnding}
        errorMessage={error ? mapEndSessionError(error.code) : null}
        onConfirm={() => endSession()}
        onDismiss={() => {
          reset();
          setConfirmOpen(false);
        }}
      />
    </div>
  );
}
